import type { Sponsor, Supporter } from '@/types/sponsors';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

const BASE = import.meta.env.BASE_URL || '/';

/**
 * 表示順のソート: sort_order=0 は登録順、0以外はその番号の位置に配置する
 * 同じ sort_order の場合は登録順（created_at）を第2ソートとして適用。登録企業を非表示にしない。
 * 例: 0,0,0,4,0,6,0,0 → 0のものは登録順、4番目にsort_order=4、6番目にsort_order=6
 */
export function sortByDisplayOrder<T extends { sort_order?: number | null; created_at?: string }>(items: T[]): T[] {
  if (items.length === 0) return [];

  const zeroItems = [...items.filter((i) => (i.sort_order ?? 0) === 0)].sort((a, b) =>
    (a.created_at || '').localeCompare(b.created_at || '')
  );
  const positioned = items.filter((i) => (i.sort_order ?? 0) !== 0);
  // 同じ sort_order が複数ある場合に備え、Map<number, T[]> で保持（第2ソート: created_at）
  const posMap = new Map<number, T[]>();
  for (const p of positioned) {
    const order = p.sort_order!;
    const arr = posMap.get(order) ?? [];
    arr.push(p);
    posMap.set(order, arr);
  }
  for (const arr of posMap.values()) {
    arr.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  }

  const n = items.length;
  const result: T[] = [];
  let zeroIdx = 0;
  for (let pos = 1; pos <= n; pos++) {
    const atPos = posMap.get(pos);
    if (atPos?.length) {
      for (const p of atPos) result.push(p);
    } else if (zeroIdx < zeroItems.length) {
      result.push(zeroItems[zeroIdx++]);
    }
  }
  while (zeroIdx < zeroItems.length) {
    result.push(zeroItems[zeroIdx++]);
  }
  for (const p of positioned) {
    const order = p.sort_order!;
    if (order > n && !result.includes(p)) result.push(p);
  }
  // 万が一取りこぼしがあれば末尾に追加（登録企業を非表示にしない）
  const resultSet = new Set(result);
  const missed = items.filter((i) => !resultSet.has(i));
  if (missed.length) {
    missed.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    result.push(...missed);
  }
  return result;
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}data/${path}`);
  if (!res.ok) throw new Error(`Failed to fetch ${path}`);
  return res.json();
}

export async function fetchSponsors(): Promise<Sponsor[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('sponsors')
      .select('*');
    if (error) {
      console.warn('Supabase sponsors fetch error:', error);
      return fetchJson<Sponsor[]>('sponsors.json');
    }
    return (data || []) as Sponsor[];
  }
  return fetchJson<Sponsor[]>('sponsors.json');
}

export async function fetchSupporters(): Promise<Supporter[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase
      .from('supporters')
      .select('*');
    if (error) {
      console.warn('Supabase supporters fetch error:', error);
      return fetchJson<Supporter[]>('supporters.json');
    }
    return (data || []) as Supporter[];
  }
  return fetchJson<Supporter[]>('supporters.json');
}
